import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { Link } from "react-router-dom";

const warningSigns = [
  "Damaged wires or frayed insulation",
  "Overloaded sockets or extension leads",
  "Burning smell from electrical appliances",
  "Flickering lights",
  "Frequent circuit breaker tripping",
];

const preventionTips = [
  {
    id: "industry-standard",
    icon: "ri-verified-badge-line",
    title: "Only Use Industry Standard Electrical Systems",
    content:
      "Most people experience frequent electrical fires because of using sub-standard electrical systems and appliances. Poor quality products are subject to constant malfunction and damage that only increases the risks of fires on the building. Make sure that all the electrical systems installed on the building including switches, power outlets and cables meet the quality standards. You should also avoid using sub-standard or cheap electrical appliances like entertainment systems and lighting to ensure electrical fire safety.",
  },
  {
    id: "regular-maintenance",
    icon: "ri-tools-line",
    title: "Regularly Check and Maintain All the Electrical Installations",
    content:
      "Although some electrical faults present a wide range of easily noticeable symptoms, others do not show any and are only detected after causing a serious problem. You do not want to experience a fire in order to know that it was caused by a fault on your electrical systems. For proper assessments and maintenance of your electrical systems on a regular basis, you should hire a professional to perform the procedures. Routine checks on the electrical installations will facilitate the early detection and resolution of potential electrical fire threats. During the assessments, any likely electrical issues should be clearly documented and reported for immediate and appropriate action.",
  },
  {
    id: "diagnose-repair",
    icon: "ri-search-line",
    title: "Properly Diagnose and Repair or Replace Electrical Faults",
    content:
      "You never know when faults on electrical systems might turn disastrous. In case you suspect any kind of fault or damage to the electrical systems and applications, make sure that they are thoroughly diagnosed and resolved as soon as possible. Regardless of the magnitude of the problem or the affected electrical components, all electrical issues should be immediately diagnosed and repaired. If the damage is beyond repair, appropriate replacements should be made. Postponing the repairs and replacements could only impact more serious problems on the systems, thereby increasing the risks of electrical fires on the building.",
  },
  {
    id: "avoid-diy",
    icon: "ri-user-settings-line",
    title: "Avoid DIY Electrical Procedures",
    content:
      "Whether you are installing a new electrical system or fixing an old one, it is always advisable to avoid DIY projects. Although it might seem like an easier and cost-effective alternative, handling electrical procedures without electrical engineering expertise and tools could only endanger your life. In some occasions, you might even end up damaging the systems, costing a lot more money to fix. Whenever you need to install electrical systems or fix issues on the devices, simply hire an electrical engineer or licensed electrician to do the job. That will ensures the procedures are conducted safely, quickly and to the best standards for better electrical safety.",
  },
  {
    id: "responsible-use",
    icon: "ri-plug-line",
    title: "Use Electrical Appliances Responsibly",
    content:
      "Mishandling electrical appliances is a significant cause of electrical fires in many homes and offices. Always observe the instructions given on the user manuals when using electrical appliances. You should also make sure that all exposed wires are protected against damage from humans, pets and environmental elements. Keep all the wires and power outlets away from water prone areas. Whenever you are using adapters, always stick to the recommended limits to avoid overloading the circuits. Besides, all idle electrical appliances should be disconnected from the power. Use bulbs and other lighting fixtures recommended for the available wattage or electricity in your home.",
  },
  {
    id: "fire-extinguisher",
    icon: "ri-fire-line",
    title: "Install a Portable Fire Extinguisher on the Building",
    content:
      "Having a portable fire extinguisher cannot prevent electrical fires but can enable you to safely and quickly put out the fires whenever they occur. However, you should be keen to buy Class C fire extinguishers that are designed for fighting electrical fires. CO2 fire extinguishers are usually the best for ensuring protection against electrical fires.",
  },
];

export default function ElectricalFireSafetyPage() {
  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: preventionTips.map((tip) => ({
              "@type": "Question",
              name: tip.title,
              acceptedAnswer: {
                "@type": "Answer",
                text: tip.content,
              },
            })),
          }),
        }}
      />

      <main>
        {/* Hero */}
        <section className="relative pt-20 min-h-[380px] md:min-h-[460px] flex items-end">
          <div className="absolute inset-0">
            <img
              src="https://storage.readdy-site.link/project_files/bad53886-d0ad-4ee3-acb4-5b763a394fe2/9b789789-40a5-48c3-8a81-70e176488bce_compressed_Depositphotos_10827891_l-2015-min.webp"
              alt="Electrical fire hazard sparking wires"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-foreground-950/50 to-foreground-950/20"></div>
          </div>
          <div className="relative z-10 w-full px-4 md:px-6 lg:px-10 py-10 md:py-14 max-w-7xl mx-auto">
            <nav aria-label="Breadcrumb" className="mb-4">
              <ol className="flex items-center gap-2 text-xs text-background-300 flex-wrap">
                <li><Link to="/" className="hover:text-background-50 transition-colors">Home</Link></li>
                <li><i className="ri-arrow-right-s-line"></i></li>
                <li><Link to="/resources" className="hover:text-background-50 transition-colors">Resources</Link></li>
                <li><i className="ri-arrow-right-s-line"></i></li>
                <li className="text-background-50 font-medium">Electrical Fire Safety</li>
              </ol>
            </nav>
            <span className="inline-block text-xs font-semibold bg-primary-500 text-background-50 rounded-full px-3 py-1 mb-4">
              Fire Prevention Guide
            </span>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-background-50 leading-tight mb-4 max-w-3xl">
              Electrical Fire Safety and <span className="text-primary-400">Prevention Tips</span>
            </h1>
            <p className="text-background-200 text-base md:text-lg max-w-2xl leading-relaxed">
              Electrical faults are among the major causes of fires in homes, businesses and industrial spaces today. Know the risks and the practical steps you can take to prevent them.
            </p>
          </div>
        </section>

        {/* Warning Signs */}
        <section className="px-4 md:px-6 lg:px-10 py-12 md:py-16 max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            <div>
              <span className="inline-block text-xs font-semibold uppercase tracking-wider text-accent-500 border border-accent-200 rounded-full px-3 py-1 mb-4">
                Know the Signs
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                Common Causes of Electrical Fires
              </h2>
              <p className="text-foreground-600 text-base leading-relaxed mb-4">
                Electrical faults are among the major causes of fires in homes, businesses and industrial spaces today. In fact, there are several kinds of electrical faults that are commonly known to start fires in most living and work places including overloaded circuits, wiring problems, and mishandling of electrical appliances among others.
              </p>
              <p className="text-foreground-600 text-base leading-relaxed mb-6">
                While you may not stop these issues from happening, there are a few tricks through which you can prevent the risks. Be alert to these warning signs that may indicate an electrical fire hazard:
              </p>
              <div className="flex flex-col gap-3">
                {warningSigns.map((sign) => (
                  <div key={sign} className="flex items-center gap-3 bg-background-100 rounded-lg px-4 py-3 border border-background-200/70">
                    <div className="w-6 h-6 flex items-center justify-center rounded-full bg-accent-100 flex-shrink-0">
                      <i className="ri-error-warning-line text-accent-600 text-xs"></i>
                    </div>
                    <span className="text-foreground-700 text-sm">{sign}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden h-72 md:h-96">
              <img
                src="https://storage.readdy-site.link/project_files/bad53886-d0ad-4ee3-acb4-5b763a394fe2/9b789789-40a5-48c3-8a81-70e176488bce_compressed_Depositphotos_10827891_l-2015-min.webp"
                alt="Electrical fire hazard sparking wires"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </section>

        {/* Prevention Tips */}
        <section className="bg-background-100 py-12 md:py-16">
          <div className="px-4 md:px-6 lg:px-10 max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <span className="inline-block text-xs font-semibold uppercase tracking-wider text-accent-500 border border-accent-200 rounded-full px-3 py-1 mb-3">
                Prevention
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                Electrical Fire Safety and <span className="text-primary-500">Prevention Tips</span>
              </h2>
            </div>
            <div className="flex flex-col gap-5">
              {preventionTips.map((tip, i) => (
                <div key={tip.id} id={tip.id} className="bg-background-50 rounded-2xl p-6 md:p-8 border border-background-200/70">
                  <div className="flex items-start gap-5">
                    <div className="flex flex-col items-center gap-2 flex-shrink-0">
                      <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100">
                        <i className={`${tip.icon} text-primary-600 text-xl`}></i>
                      </div>
                      <span className="text-xs font-bold text-foreground-400">0{i + 1}</span>
                    </div>
                    <div>
                      <h3 className="font-heading text-lg md:text-xl font-bold text-foreground-950 mb-3">
                        <a href={`#${tip.id}`}>{tip.title}</a>
                      </h3>
                      <p className="text-foreground-600 text-base leading-relaxed">{tip.content}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="px-4 md:px-6 lg:px-10 py-12 md:py-16 max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
            Get Industry-Standard Fire Safety Products in Singapore
          </h2>
          <p className="text-foreground-600 text-base max-w-xl mx-auto mb-8">
            In case you are looking to buy a portable fire extinguisher or any other fire safety products Singapore, simply talk to Ezzogenics. We stock quality and industry-standard fire protection systems and fire safety products for both living and work spaces.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/products"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              Browse Fire Extinguishers
              <i className="ri-arrow-right-line"></i>
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-primary-300 text-primary-600 text-sm font-semibold hover:bg-primary-50 transition-colors whitespace-nowrap"
            >
              Contact Us
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}